import { createRouter, createWebHistory } from 'vue-router'
import Layout from '@/layout/index.vue'
import { useUserStore } from '@/stores/user'
import { getDynamicContentRoutes } from '@/api/content'
import type { DynamicContentRoutesResult, UrlRecordDto } from '@/types/content'

let dynamicContentRoutesLoaded = false
let dynamicContentRoutesLoading: Promise<void> | null = null

const toBool = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true'
  return false
}

const normalizeDynamicPath = (item: UrlRecordDto): string => {
  const raw = item.Slug ?? item.slug ?? ''
  const trimmed = String(raw).trim()
  if (!trimmed) return ''
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

const normalizeDynamicTitle = (item: UrlRecordDto): string => {
  return item.title ?? item.Title ?? '内容详情'
}

const normalizeDynamicPublic = (item: UrlRecordDto): boolean => {
  const flag = item.isPublic ?? item.IsPublic
  return flag === undefined || flag === null ? false : toBool(flag)
}

const normalizeDynamicUseLayout = (item: UrlRecordDto): boolean => {
  const withoutLayout = item.withoutLayout ?? item.WithoutLayout
  if (withoutLayout !== undefined && withoutLayout !== null) {
    return !toBool(withoutLayout)
  }

  const useLayout = item.useLayout ?? item.UseLayout
  return useLayout === undefined || useLayout === null ? true : toBool(useLayout)
}

const normalizeEntityId = (item: UrlRecordDto): number | string | null => {
  const raw = item.EntityId ?? item.entityId
  if (raw === undefined || raw === null || raw === '') return null
  return raw
}

const extractRouteItems = (payload: UrlRecordDto[] | DynamicContentRoutesResult): UrlRecordDto[] => {
  if (Array.isArray(payload)) {
    return payload
  }

  const records = payload.UrlRecords ?? payload.urlRecords
  if (Array.isArray(records) && records.length > 0) {
    return records
  }

  const slugs = payload.Slugs ?? payload.slugs ?? []
  if (!Array.isArray(slugs)) {
    return []
  }

  return slugs
    .filter((slug): slug is string => typeof slug === 'string' && slug.trim().length > 0)
    .map((slug) => ({
      Slug: slug,
      Title: '内容详情',
      IsPublic: false
    }))
}

const buildDynamicRouteName = (path: string): string => {
  return `dynamic-content-${path.replace(/^\//, '').replace(/[^a-zA-Z0-9]/g, '-')}`
}

const registerDynamicContentRoutes = async (routerInstance: ReturnType<typeof createRouter>) => {
  if (dynamicContentRoutesLoaded) return
  if (dynamicContentRoutesLoading) {
    await dynamicContentRoutesLoading
    return
  }

  dynamicContentRoutesLoading = (async () => {
    try {
      const routePayload = await getDynamicContentRoutes()
      const routeItems = extractRouteItems(routePayload)
      routeItems.forEach((item) => {
        const path = normalizeDynamicPath(item)
        if (!path) return

        const routeName = buildDynamicRouteName(path)
        const existed = routerInstance.getRoutes().some((route) => route.path === path || route.name === routeName)
        if (existed) return

        const useLayout = normalizeDynamicUseLayout(item)
        const routeRecord = {
          path: useLayout ? path.replace(/^\//, '') : path,
          name: routeName,
          component: () => import('@/views/content/dynamic-content.vue'),
          meta: {
            title: normalizeDynamicTitle(item),
            isDynamicContent: true,
            entityId: normalizeEntityId(item),
            public: normalizeDynamicPublic(item)
          }
        }

        if (useLayout) {
          routerInstance.addRoute('RootLayout', routeRecord)
        } else {
          routerInstance.addRoute(routeRecord)
        }
      })

    } catch {
      // 动态路由接口失败时，保持静态路由可用。
    } finally {
      dynamicContentRoutesLoaded = true
      dynamicContentRoutesLoading = null
    }
  })()

  await dynamicContentRoutesLoading
}

const router = createRouter
({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior() {
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'RootLayout',
      component: Layout, // 父级路由使用 Layout 组件
      children: [
        {
          path: 'password-recovery',
          name: 'PasswordRecovery',
          component: () => import('@/views/account/password-recovery.vue'),
          meta: { title: '找回密码' }
        },
        {
          path: '', // 默认子路由（即首页）
          name: 'home',
          component: () => import('@/views/home/index.vue'),
          meta: { title: '首页' }
        },
        {
          path: 'login',
          name: 'Login',
          component: () => import('@/views/login/index.vue'),
          meta: { title: '登录', public: true }
        },
        {
          path: 'delivery-orders',
          alias: ['orders'],
          name: 'root-delivery-orders',
          component: () => import('@/views/order/delivery-orders.vue'),
          meta: { title: '配送订单' }
        },
        {
          path: 'delivery-orders/detail/:id',
          name: 'RootOrderDetailDelivery',
          component: () => import('@/views/order/order-detail.vue'),
          meta: { title: '订单详情' }
        },
        {
          path: 'pickup-orders',
          name: 'root-pickup-orders',
          component: () => import('@/views/order/pickup-orders.vue'),
          meta: { title: '自提订单' }
        },
        {
          path: 'pickup-orders/detail/:id',
          name: 'RootOrderDetailPickup',
          component: () => import('@/views/order/order-detail.vue'),
          meta: { title: '订单详情' }
        },
        {
          path: 'products',
          name: 'contract-products',
          component: () => import('@/views/product/contract-products.vue'),
          meta: { title: '合约产品下单' }
        },
        {
          path: 'tankdetails/:productId/:shipToId/:equipmentInfoId?',
          name: 'tank-detail',
          component: () => import('@/views/product/tank-detail.vue'),
          meta: { title: '储罐详情' }
        },
        {
          path: 'register-option',
          name: 'register-option',
          component: () => import('@/views/account/register-option.vue'),
          meta: { title: '注册选项' }
        },
        {
          path: 'wanttosign',
          name: 'wanttosign',
          component: () => import('@/views/account/want-to-sign.vue'),
          meta: { title: '我想签约', public: true }
        },
        {
          path: 'register-contract',
          name: 'register-contract',
          component: () => import('@/views/account/register-contract.vue'),
          meta: { title: '合同用户注册' }
        },
        {
          path: 'customer-center',
          name: 'customer-center',
          component: () => import('@/views/customer-center/index.vue'),
          meta: { title: '客户中心' },
          redirect: '/customer-center/basic-info', // 默认跳转
          children: [
            {
              path: 'basic-info',
              name: 'basic-info',
              component: () => import('@/views/account/basic-info.vue'),
              meta: { title: '基本信息' }
            },
            {
              path: 'ship-tos',
              name: 'ship-tos',
              component: () => import('@/views/account/ship-tos.vue'),
              meta: { title: '地址管理' }
            },
            {
              path: 'equipment-query-modern',
              name: 'equipment-query-modern',
              component: () => import('@/views/account/equipment-query-modern.vue'),
              meta: { title: '设备查询' }
            },
            {
              path: 'delivery-contacts/:equipmentId',
              name: 'DeliveryContactsManage',
              component: () => import('@/views/account/delivery-contacts.vue'),
              meta: { title: '送货联系人' }
            },
            {
              path: 'messages',
              name: 'messages',
              component: () => import('@/views/message/messages.vue'),
              meta: { title: '消息中心' }
            },
            {
              path: 'feedback',
              name: 'feedback',
              component: () => import('@/views/account/feedback.vue'),
              meta: { title: '我要反馈' }
            },
            {
            path: 'message-detail/:id',
            name: 'MessageDetail',
            component: () => import('@/views/message/message-detail.vue'),
            meta: { title: '消息详情' }
           },
            {
              path: 'invoices',
              name: 'invoices',
              component: () => import('@/views/invoice/invoices.vue'),
              meta: { title: '发票详单' }
            },{
              path: 'statements',
              name: 'statements',
              component: () => import('@/views/statement/statements.vue'),
              meta: { title: '对账单' }
            },
            {
              path: 'delivery-orders',
              name: 'delivery-orders',
              component: () => import('@/views/order/delivery-orders.vue'),
              meta: { title: '配送订单' }
            },
            {
              path: 'delivery-orders/detail/:id',
              name: 'OrderDetailDelivery',
              component: () => import('@/views/order/order-detail.vue'),
              meta: { title: '订单详情' }
            },
            {
              path: 'pickup-orders',
              name: 'pickup-orders',
              component: () => import('@/views/order/pickup-orders.vue'),
              meta: { title: '自提订单' }
            },{
              path: 'pickup-orders/detail/:id',
              name: 'OrderDetailPickup',
              component: () => import('@/views/order/order-detail.vue'),
              meta: { title: '订单详情' }
            },{
              path: 'order-detail/:id',
              name: 'OrderDetail',
              component: () => import('@/views/order/order-detail.vue'),
              meta: { title: '订单详情' }
            },
            {
              path: 'distribution-plannings',
              name: 'DistributionPlannings',
              component: () => import('@/views/distribution-planning/distribution-plannings.vue'),
              meta: { title: '历史分销' }
            },
            {
              path: 'draft-distribution-plannings',
              name: 'DraftDistributionPlannings',
              component: () => import('@/views/distribution-planning/draft-distribution-plannings.vue'),
              meta: { title: '分销计划' }
            },
            {
              path: 'supplementary-draft-distribution-plannings',
              name: 'SupplementaryDraftDistributionPlannings',
              component: () => import('@/views/distribution-planning/supplementary-draft-distribution-plannings.vue'),
              meta: { title: '分销补单' }
            },
            {
              path: 'customers',
              name: 'customers',
              component: () => import('@/views/customer/customers.vue'),
              meta: { title: '账号管理' }
            },
            {
              path: 'customers/create',
              name: 'customer-create',
              component: () => import('@/views/customer/customer-create.vue'),
              meta: { title: '新建账号' }
            },
          ]
        }
      ]
    }
  ]
})

router.beforeEach(async (to, from) => {
  // 获取本地存储的 token
 const userStore = useUserStore() // 获取 store 实例
  await registerDynamicContentRoutes(router)

  // 动态添加路由后，需要触发一次重匹配。
  if (to.matched.length === 0) {
    const nowMatched = router.resolve(to.fullPath)
    if (nowMatched.matched.length > 0) {
      return to.fullPath
    }
  }
  
  const hasToken = userStore.token 
  const publicPaths = ['/', '/login', '/register-option', '/register-contract', '/password-recovery']
  const isPublicRoute = publicPaths.includes(to.path) || to.matched.some((record) => record.meta?.public)
  if (isPublicRoute) {
    if (hasToken && to.path === '/login') return { path: '/' }
    return true
  }

  // 如果目标路由不是登录页，且用户没有 token，则强制跳转到登录页
  if (to.path === '/login') {
    // 如果已登录且要去登录页，重定向到首页
    if (hasToken) return { path: '/' }
    else return true // 允许访问登录页
  } else {
    // 访问其他页面，检查是否有 token
    if (hasToken) {
      return true
    } else {
      // 没 token，强制去登录
      return { path: '/login' }
    }
  }
})

export default router